import {
  CardsThree,
  ChatsCircle,
  Checks,
  ClockCounterClockwise,
  type Icon,
  ImageSquare,
  Lightning,
  LinkSimple,
  NoteBlank,
  PaperPlaneTilt,
  PencilSimpleLine,
  SlackLogo,
  Trash,
  UserCircleGear,
  UserGear,
  Users,
  UsersFour,
  WebhooksLogo,
} from "@phosphor-icons/react";

import type { TagVariantType } from "common/components/ui/Tag";
import type { ApiKeyPermission, SubscriptionType } from "common/types";

import type { InvoiceStatus } from "./types";

import AgencyCoverImage from "assets/images/plans/agency-cover.webp";
import AvatarCoverImage from "assets/images/plans/avatar-cover.webp";
import OutreachCoverImage from "assets/images/plans/outreach-cover.webp";

export const BILLING_STATUSES: Record<
  InvoiceStatus,
  { label: string; bgClassname: string }
> = {
  paid: { label: "Paid", bgClassname: "bg-green-400" },
  posted: { label: "Posted", bgClassname: "bg-black-400" },
  pending: { label: "Invite sent", bgClassname: "bg-black-400" },
  not_paid: { label: "Not paid", bgClassname: "bg-red-500" },
  payment_due: { label: "Payment due", bgClassname: "bg-yellow-400" },
  voided: { label: "Voided", bgClassname: "bg-black-400" },
} as const;

export const API_KEYS_PERMISSIONS: Record<
  ApiKeyPermission,
  {
    label: string;
    tagVariant: TagVariantType;
    textColor: string;
    description?: string;
  }
> = {
  all: {
    label: "All",
    tagVariant: "primary",
    textColor: "text-purple-600",
    description:
      "“All” permission level allows full access to Aimfox resources. This key will allow any API call from our documentation",
  },
  "read-only": {
    label: "Read Only",
    tagVariant: "secondary",
    textColor: "text-purple-400",
    description:
      "“Read only” permission level allows read only access to Aimfox resources. This key will only allow access to GET API calls from our documentation",
  },
} as const;

export const API_KEYS_ACTIONS = {
  edit: { icon: PencilSimpleLine, label: "Edit API key's name" },
  remove: { icon: Trash, label: "Delete API key", isDanger: true },
} as const;

export const WEBHOOKS_ACTIONS = {
  edit: { icon: PencilSimpleLine, label: "Edit webhook" },
  test: { icon: WebhooksLogo, label: "Test webhook" },
  delete: { icon: Trash, label: "Delete webhook", isDanger: true },
} as const;

export const CLIENT_ACTIONS = {
  permissions: { icon: UserGear, label: "Update permissions" },
  loginLink: { icon: LinkSimple, label: "Generate login link" },
  remove: { icon: Trash, label: "Remove from workspace", isDanger: true },
} as const;

export const AVATAR_UPSELL_CARDS: { id: string; title: string; icon: Icon }[] =
  [
    {
      id: "outreach-seat",
      title: "Outreach plan included",
      icon: Lightning,
    },
    {
      id: "customization",
      title: "Profile Customization",
      icon: ImageSquare,
    },
    {
      id: "days-delivery",
      title: "Delivery in 1-2 business days",
      icon: ClockCounterClockwise,
    },
    {
      id: "replacement-guarantee",
      title: "Replacement guarantee",
      icon: Checks,
    },
  ];

export const ACTIVATE_PLAN_CONFIG: Record<
  SubscriptionType,
  {
    title: string;
    subtitle: string;
    price: string;
    pricePeriod: string;
    backgroundClassName: string;
    upsellCards: { id: string; title: string; icon: Icon }[];
  }
> = {
  rental: {
    title: "Avatar plan",
    subtitle: "Use pre-warmed avatars",
    price: "$129",
    pricePeriod: "/ month",
    backgroundClassName: "bg-[url(/images/plans/settings/avatar-bg.webp)]",
    upsellCards: AVATAR_UPSELL_CARDS,
  },
  agency: {
    title: "Agency plan",
    subtitle: "Scale across workspaces",
    price: "$499",
    pricePeriod: "/ month",
    backgroundClassName: "bg-[url(/images/plans/settings/agency-bg.webp)]",
    upsellCards: [
      { id: "slack-onboarding", title: "Slack onboarding", icon: SlackLogo },
      { id: "whitelabel", title: "Full whitelabel", icon: NoteBlank },
      { id: "collaboration", title: "Team collaboration", icon: UsersFour },
      {
        id: "migration",
        title: "Profile migration assistance",
        icon: UserCircleGear,
      },
    ],
  },
  user: {
    title: "Outreach Plan",
    subtitle: "Automate LinkedIn outreach",
    price: "$49",
    pricePeriod: "/ month",
    backgroundClassName: "bg-[url(/images/plans/settings/outreach-bg.webp)]",
    upsellCards: [
      {
        id: "connect",
        title: "Seamlessly connect accounts",
        icon: Users,
      },
      {
        id: "inbound-outbound",
        title: "Inbound & outbound campaigns",
        icon: PaperPlaneTilt,
      },
      {
        id: "unified-inbox",
        title: "Unified inbox access",
        icon: ChatsCircle,
      },
      {
        id: "safe",
        title: "3rd party integrations",
        icon: CardsThree,
      },
    ],
  },
};

export const PLAN_SETTINGS_BACKGROUNDS = {
  agency: "bg-[url(/images/plans/settings/agency-bg.webp)]",
  outreach: "bg-[url(/images/plans/settings/outreach-bg.webp)]",
  avatar: "bg-[url(/images/plans/settings/avatar-bg.webp)]",
} as const;

export const PLAN_COVER_IMAGES: Record<SubscriptionType, string> = {
  rental: AvatarCoverImage,
  agency: AgencyCoverImage,
  user: OutreachCoverImage,
};

export const ACTIVATE_PLAN_UPSELL_ICON_COLORS: Record<
  SubscriptionType,
  { bg: string; text: string }
> = {
  rental: { bg: "bg-purple-50", text: "text-purple-400" },
  agency: { bg: "bg-blue-50", text: "text-blue-500" },
  user: { bg: "bg-orange-50", text: "text-orange-400" },
};
